import { Dialog } from "@mui/material";
import { memo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import type { ApiResponse } from "@entrilia/shared/api/types";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import biApi from "../../../../../api/biApi";
import { WidgetType } from "../../../../../api/types/dashboardsTypes";
import {
  getEditUiReportUrl,
  getUiReportsUrl,
  getViewUiReportUrl,
} from "../../../reporting/report-lists/reports/utilities/editReportUrl";
import EditCommonDataProvider from "../../../reporting/sv/components/builder/common/EditCommonDataProvider";
import type { BiApiClient } from "../../../reporting/sv/contexts/ApiClientProviderContext";
import { BiApiClientProvider } from "../../../reporting/sv/contexts/BiApiClientProvider";
import { SupportedReportTypesContextProvider } from "../../../reporting/sv/contexts/SupportedReportTypesContextProvider";
import { useClientManagement } from "../../../reporting/sv/hooks/useClientManagement";
import { useDefineEditReportData } from "../../../reporting/sv/hooks/useDefineEditReportData";
import SmartViewAppPage from "../../../reporting/sv/SmartViewAppPage";
import { selectCurrentReport } from "../../../reporting/sv/store/selectors/currentReportSelectors";
import { CurrentTileContextProvider } from "../../contexts/CurrentTileContextProvider";
import { useDashboardContext } from "../../contexts/DashboardContext";
import EditReportPage from "./EditReportPage";
import ReportNotFoundOrAccessForbiddenPage from "./ReportNotFoundOrAccessForbiddenPage";
import { smartViewWidgetReportTypes } from "./utils/smartViewWidgetReportTypes";

export const SmartViewWidgetReportEditorApp = () => {
  return (
    <Dialog open={true} fullScreen sx={{ m: 2 }} slotProps={{ paper: { sx: { borderRadius: 1 } } }}>
      <SmartViewAppPage />
    </Dialog>
  );
};

export const SmartViewWidgetReportEditor = () => {
  const { widgetId } = useParams();
  const { tileObjects } = useDashboardContext();
  const { clientInfo } = useClientManagement();
  const tile = tileObjects.find((tile) => tile.id === widgetId);
  const widget = tile?.item?.widget;

  if (!tile || !widget || !clientInfo) {
    return <InlineLoader />;
  }

  if (widget.type === WidgetType.Filter) {
    return <>Not implemented</>;
  }

  return (
    <BiApiClientProvider<BiApiClient>
      api={{
        updateReport: biApi.dashboardReports.updateReport,
        addReport: biApi.dashboardReports.addReport,
        createDraftReport: biApi.dashboardReports.createDraftReport,
        getReport: biApi.dashboardReports.getReport,
        runTabularReport: biApi.dashboardReports.runTabularReport,
        buildTabularReport: biApi.dashboardReports.buildTabularReport,
        buildTileReport: biApi.dashboardReports.buildTileReport,
        buildChartReport: biApi.dashboardReports.buildChartReport,
        buildPivotReport: biApi.dashboardReports.buildPivotReport,
        renameReport: biApi.dashboardReports.renameReport,
        changeReportGroup: createDummyApiResponse,
        deleteReports: biApi.dashboardReports.deleteReports,
        getReportAuthorizationInfo: biApi.dashboardReports.getReportAuthorizationInfo,
        getReports: biApi.dashboardReports.getReports,
        getReportConfigurationForRunning: biApi.dashboardReports.getReportConfigurationForRunning,
        validateName: biApi.dashboardReports.validateReportName,
        getReportSharing: biApi.dashboardReports.getReportSharing,
        updateReportSharing: biApi.dashboardReports.updateReportSharing,
        getEditReportUrl: getEditUiReportUrl,
        getViewReportUrl: getViewUiReportUrl,
        getReportsUrl: getUiReportsUrl,
        getReportHistory: biApi.dashboardReports.getReportHistory,
      }}
    >
      <CurrentTileContextProvider tile={tile}>
        <SupportedReportTypesContextProvider supportedReportTypes={smartViewWidgetReportTypes}>
          <ReportEditor reportId={widget.reportId} widgetId={widget.widgetId} />
        </SupportedReportTypesContextProvider>
      </CurrentTileContextProvider>
    </BiApiClientProvider>
  );
};

const ReportEditorComponent = ({ reportId, widgetId }: { reportId: string; widgetId: string }) => {
  const currentReport = useSelector(selectCurrentReport);

  const { showReportNotFound, isSwitchingReport, isFetching } = useDefineEditReportData(reportId);

  if (showReportNotFound) {
    return <ReportNotFoundOrAccessForbiddenPage />;
  }

  if (isSwitchingReport || isFetching || currentReport === undefined) {
    return <InlineLoader text="Loading metadata, it won't take long..." />;
  }

  return (
    <EditCommonDataProvider>
      <EditReportPage widgetId={widgetId} />
    </EditCommonDataProvider>
  );
};

const ReportEditor = memo(ReportEditorComponent);

const createDummyApiResponse = <T,>(): Promise<ApiResponse<T>> => Promise.resolve({ success: true, data: {} as T });
